import { marked } from 'marked'
import DOMPurify from 'dompurify'

/**
 * Markdown das Novidades em HTML pronto para o v-html: o marked converte, o DOMPurify limpa e os
 * ajustes de leitura entram por cima do HTML já limpo.
 *
 * As opções do marked vão por chamada. Os padrões globais ficam como estão, porque o ChatFlutuante
 * depende deles.
 */

const OPCOES_DO_MARKED = { breaks: true, headerIds: false }

const FECHO = /em caso de d[uú]vidas|[àa] disposi[cç][aã]o para ajudar/i
const EMOJI = /\p{Extended_Pictographic}|️|‍/gu

export function markdownParaHtmlSeguro(markdown: string): string {
	const html = marked.parse(markdown || '', OPCOES_DO_MARKED) as string
	return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) as string
}

function rotuloLimpo(texto: string) {
	return (texto || '')
		.replace(EMOJI, '')
		.replace(/\s+/g, ' ')
		.replace(/^[\s:]+|[\s:]+$/g, '')
		.trim()
}

function ehTitulo(el: Element | null) {
	return !!el && /^H[1-6]$/.test(el.tagName)
}

function paragrafoSoComImagem(el: Element | null) {
	return !!el && el.tagName === 'P' && !el.textContent?.trim() && el.querySelectorAll('img').length > 0
}

function ehFecho(el: Element | null) {
	return !!el && el.tagName === 'P' && FECHO.test(el.textContent || '')
}

/** Subtítulos viram rótulo; "Importante" vira um quadro de aviso com o que vem logo abaixo. */
function ajustarSubtitulos(raiz: DocumentFragment) {
	for (const h of Array.from(raiz.querySelectorAll('h1, h2, h3, h4, h5, h6'))) {
		const texto = rotuloLimpo(h.textContent || '')
		if (!texto) {
			h.remove()
			continue
		}
		if (!/^importante$/i.test(texto)) {
			const rotulo = document.createElement('h4')
			rotulo.className = 'nov-rotulo'
			rotulo.textContent = texto
			h.replaceWith(rotulo)
			continue
		}
		const quadro = document.createElement('div')
		quadro.className = 'nov-importante'
		const titulo = document.createElement('div')
		titulo.className = 'nov-importante__titulo'
		titulo.textContent = 'Importante'
		quadro.appendChild(titulo)
		h.before(quadro)
		h.remove()
		let proximo = quadro.nextElementSibling
		while (proximo && !ehTitulo(proximo) && !paragrafoSoComImagem(proximo) && !ehFecho(proximo)) {
			const seguinte = proximo.nextElementSibling
			quadro.appendChild(proximo)
			proximo = seguinte
		}
	}
}

/** Imagem vira figura ampliável; duas ou mais seguidas viram galeria. */
function ajustarImagens(raiz: DocumentFragment) {
	for (const img of Array.from(raiz.querySelectorAll('img'))) {
		img.setAttribute('loading', 'lazy')
		img.setAttribute('decoding', 'async')
		img.setAttribute('tabindex', '0')
		img.setAttribute('data-ampliavel', '')
		if (!img.hasAttribute('alt')) img.setAttribute('alt', '')
		const pai = img.parentElement
		const sozinha = !!pai && pai.tagName === 'P' && !pai.textContent?.trim() && pai.querySelectorAll('img').length === 1
		const figura = document.createElement('figure')
		figura.className = 'nov-fig'
		const largura = Number(img.getAttribute('width') || 0)
		if (largura) {
			figura.classList.add('nov-fig--estreita')
			figura.style.setProperty('--nov-largura', `${largura}px`)
			img.removeAttribute('width')
		}
		if (sozinha && pai) pai.replaceWith(figura)
		else img.replaceWith(figura)
		figura.appendChild(img)
		const legenda = (img.getAttribute('alt') || '').trim()
		if (legenda) {
			const fc = document.createElement('figcaption')
			fc.textContent = legenda
			figura.appendChild(fc)
		}
	}

	// Print estreito (com width) seguido de lista: lado a lado, os números da lista são os do print
	for (const fig of Array.from(raiz.querySelectorAll('figure.nov-fig--estreita'))) {
		if (!fig.parentNode || fig.closest('.nov-lado')) continue
		const figuras = [fig]
		let proximo = fig.nextElementSibling
		while (proximo && proximo.matches('figure.nov-fig--estreita')) {
			figuras.push(proximo)
			proximo = proximo.nextElementSibling
		}
		if (!proximo || !/^(OL|UL)$/.test(proximo.tagName)) continue
		const lado = document.createElement('div')
		lado.className = 'nov-lado' + (figuras.length > 1 ? ' nov-lado--duas' : '')
		const colunaDeFiguras = document.createElement('div')
		colunaDeFiguras.className = 'nov-lado__figuras'
		const colunaDeTexto = document.createElement('div')
		colunaDeTexto.className = 'nov-lado__texto'
		fig.before(lado)
		figuras.forEach(f => colunaDeFiguras.appendChild(f))
		colunaDeTexto.appendChild(proximo)
		lado.appendChild(colunaDeFiguras)
		lado.appendChild(colunaDeTexto)
	}

	for (const fig of Array.from(raiz.querySelectorAll('figure.nov-fig'))) {
		if (!fig.parentNode || fig.closest('.nov-galeria, .nov-lado') || fig.classList.contains('nov-fig--estreita')) continue
		const grupo = [fig]
		let proximo = fig.nextElementSibling
		while (proximo && proximo.matches('figure.nov-fig:not(.nov-fig--estreita)')) {
			grupo.push(proximo)
			proximo = proximo.nextElementSibling
		}
		if (grupo.length < 2) continue
		const galeria = document.createElement('div')
		galeria.className = `nov-galeria nov-galeria--${Math.min(grupo.length, 3)}`
		fig.before(galeria)
		grupo.forEach(f => galeria.appendChild(f))
	}
}

/** Lista numerada ao lado ou logo depois de um print: marcador igual ao do print. */
function ajustarMarcadores(raiz: DocumentFragment) {
	for (const ol of Array.from(raiz.querySelectorAll('ol'))) {
		let anterior = ol.previousElementSibling
		let depoisDePrint = false
		while (anterior && !ehTitulo(anterior)) {
			if (anterior.matches('figure, .nov-lado, .nov-galeria')) {
				depoisDePrint = true
				break
			}
			anterior = anterior.previousElementSibling
		}
		const naLateral = !!ol.parentElement && ol.parentElement.classList.contains('nov-lado__texto')
		if (!naLateral && !depoisDePrint) continue
		ol.classList.add('nov-marcadores')
		const inicio = Number(ol.getAttribute('start') || 1)
		Array.from(ol.children).forEach((li, i) => li.setAttribute('data-n', String(inicio + i)))
	}
}

function ajustarLinks(raiz: DocumentFragment) {
	for (const a of Array.from(raiz.querySelectorAll('a[href]'))) {
		const href = a.getAttribute('href') || ''
		if (/^https?:\/\//i.test(href)) {
			a.setAttribute('target', '_blank')
			a.setAttribute('rel', 'noopener noreferrer')
		}
	}
}

/**
 * HTML do corpo de um item (ou da introdução) com o acabamento de leitura: rótulos "Como funciona",
 * quadro "Importante", frase de fecho discreta, figuras, galeria, print ao lado da lista numerada e
 * marcadores.
 */
export function renderizarCorpo(markdown: string): string {
	const modelo = document.createElement('template')
	modelo.innerHTML = markdownParaHtmlSeguro(markdown)
	const raiz = modelo.content
	ajustarSubtitulos(raiz)
	raiz.querySelectorAll('p').forEach(p => {
		if (ehFecho(p)) p.classList.add('nov-fecho')
	})
	ajustarLinks(raiz)
	ajustarImagens(raiz)
	ajustarMarcadores(raiz)
	const saida = document.createElement('div')
	saida.appendChild(raiz)
	return saida.innerHTML
}
